import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Col, Button, Spinner, Container,
    Modal, Nav, NavItem, NavLink, TabContent, TabPane, Table
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, mediaUrl } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3 } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { Edit, Plus, Trash2, Eye, Settings, FileText, Shield } from 'react-feather';
import { useAuth } from "../../Context/AuthContext";

interface SettingData {
    id: number;
    company_name: string;
    address?: string;
    email: string;
    contact_number: string;
    currency: string;
    logo: string | null;
    base_hourly_rate: string | number;
    bystander_base_hourly_rate: string | number;
}

interface FullSettingData extends SettingData {
    user_term_condition?: string;
    user_privacy_policy?: string;
    bystander_term_condition?: string;
    bystander_privacy_policy?: string;
}

export default function ViewGeneralSettings() {
    const [data, setData] = useState<SettingData[]>([]);
    const [loading, setLoading] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [viewData, setViewData] = useState<FullSettingData | null>(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchData();
    }, [refresh]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response: any = await apiPost("settings/list", {});
            if (response && response.data) {
                setData(response.data);
            } else {
                setData(response);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load settings data");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => navigate(`${process.env.PUBLIC_URL}/add_general_settings`);
    const handleEdit = (id: number) => navigate(`${process.env.PUBLIC_URL}/edit_general_settings`, { state: { id } });

    const toggleViewModal = () => {
        setViewModal(!viewModal);
        if (!viewModal) { // If opening, reset active tab
            setActiveTab('1');
        } else { // If closing, clear view data
            setViewData(null);
        }
    };

    const handleView = async (id: number) => {
        setViewModal(true);
        setViewLoading(true);
        setActiveTab('1');
        try {
            const data: any = await apiPost('settings/viewbyid', { id });
            setViewData(data);
        } catch (err) {
            toast.error('Failed to load settings details');
            setViewModal(false);
        } finally {
            setViewLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost(`settings/delete`, { id });
                    toast.success("Settings have been deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete settings", { autoClose: 1000 });
                }
            }
        });
    };

    const columns = [
        {
            name: <div><span className="fw-bold">Sl. No.</span></div>,
            selector: (row: SettingData, rowIndex?: number) => (rowIndex || 0) + 1,
            sortable: true,
        },
        {
            name: <div><span className="fw-bold">Logo</span></div>,
            cell: (row: SettingData) => (
                <div style={{ padding: '5px 0' }}>
                    {row.logo ? (
                        <img
                            src={`${mediaUrl}${row.logo}`}
                            alt="Logo"
                            style={{ maxWidth: '80px', maxHeight: '30px', objectFit: 'contain' }}
                        />
                    ) : (
                        <span className="text-muted small">No Logo</span>
                    )}
                </div>
            ),
        },
        {
            name: <div><span className="fw-bold">Company Name</span></div>,
            selector: (row: SettingData) => row.company_name,
            sortable: true,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Email</span></div>,
            selector: (row: SettingData) => row.email,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Contact</span></div>,
            selector: (row: SettingData) => row.contact_number,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Currency</span></div>,
            selector: (row: SettingData) => row.currency,
            sortable: true,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Hourly Rate</span></div>,
            selector: (row: SettingData) => row.base_hourly_rate,
            sortable: true,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Bystander Rate</span></div>,
            selector: (row: SettingData) => row.bystander_base_hourly_rate,
            sortable: true,
            wrap: true,
        },
        {
            name: <div><span className="fw-bold">Actions</span></div>,
            cell: (row: SettingData) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('general-settings.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row.id)} title="View">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('general-settings.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('general-settings.delete') && (
                        <Button size="sm" color="danger" onClick={() => row.id && handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="General Settings" parent="Master Settings" title="General Settings" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            {!hasPermission('general-settings.view') ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fa fa-lock fa-2x mb-2 d-block"></i>
                                    You do not have permission to view General Settings.
                                </div>
                            ) : (
                                <>
                                    <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-4">
                                        <H3 className="text-primary">System Settings Overview</H3>
                                        {data.length === 0 && hasPermission('general-settings.add') && !loading && (
                                            <Btn color="primary" onClick={handleAdd}>
                                                <Plus size={16} className="me-2" /> Add Settings
                                            </Btn>
                                        )}
                                    </Col>

                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            customStyles={customStyles}
                                            noDataComponent={
                                                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                                    <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                                        <CardBody>
                                                            <h6 className="fw-semibold mb-1">No Settings Found</h6>
                                                            <small className="text-muted">You haven't created any settings yet.</small>
                                                        </CardBody>
                                                    </Card>
                                                </div>
                                            }
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* Premium General Settings View Modal */}
            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(!viewModal)}
                size="lg"
                centered
                scrollable
            >
                {viewData && (
                    <div style={{
                        background: 'linear-gradient(135deg, #1e6ec8 0%, #0d4fa0 100%)',
                        padding: '1.5rem',
                        position: 'relative',
                        width: '100%'
                    }}>
                        <button
                            className="btn-close btn-close-white"
                            type="button"
                            onClick={() => setViewModal(false)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            {viewData.logo ? (
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0,
                                    padding: '6px', overflow: 'hidden'
                                }}>
                                    <img src={`${mediaUrl}${viewData.logo}`} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                </div>
                            ) : (
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0
                                }}>
                                    <Settings size={28} color="white" />
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <h3 style={{ color: 'white', fontWeight: 700, fontSize: '1.4rem', margin: 0, marginBottom: '0.25rem' }}>
                                    {viewData.company_name}
                                </h3>
                                <div style={{ display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span>{viewData.email}</span>
                                    <span>•</span>
                                    <span style={{ fontWeight: 600 }}>{viewData.currency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="modal-body p-0">
                    {viewLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                            <Spinner color="primary" />
                        </div>
                    ) : viewData ? (
                        <>
                            <div className="premium-modal-tabs">
                                <Nav tabs style={{ marginTop: '0.5rem' }}>
                                    <NavItem>
                                        <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')} style={{ cursor: 'pointer' }}>
                                            <Settings size={14} className="me-2" /> Company Info
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')} style={{ cursor: 'pointer' }}>
                                            <FileText size={14} className="me-2" /> User Policies
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={activeTab === '3' ? 'active' : ''} onClick={() => setActiveTab('3')} style={{ cursor: 'pointer' }}>
                                            <Shield size={14} className="me-2" /> Bystander Policies
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </div>

                            <div className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
                                <TabContent activeTab={activeTab}>
                                    {/* Tab 1 — Company Info */}
                                    <TabPane tabId="1">
                                        <div className="bg-white rounded shadow-sm overflow-hidden">
                                            <Table borderless className="mb-0">
                                                <tbody>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold" style={{ width: '40%' }}>COMPANY NAME</td>
                                                        <td className="pe-4 py-3 text-end fw-bold text-primary">{viewData.company_name}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold">EMAIL ADDRESS</td>
                                                        <td className="pe-4 py-3 text-end fw-bold">{viewData.email}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold">CONTACT NUMBER</td>
                                                        <td className="pe-4 py-3 text-end fw-bold">{viewData.contact_number || '—'}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold">CURRENCY</td>
                                                        <td className="pe-4 py-3 text-end fw-bold">{viewData.currency}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold">BASE HOURLY RATE</td>
                                                        <td className="pe-4 py-3 text-end fw-bold text-success">{viewData.base_hourly_rate}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-4 py-3 text-muted fw-bold">BYSTANDER HOURLY RATE</td>
                                                        <td className="pe-4 py-3 text-end fw-bold text-success">{viewData.bystander_base_hourly_rate}</td>
                                                    </tr>
                                                    {viewData.address && (
                                                        <tr className="border-bottom">
                                                            <td className="ps-4 py-3 text-muted fw-bold">ADDRESS</td>
                                                            <td className="pe-4 py-3 text-end fw-semibold">{viewData.address}</td>
                                                        </tr>
                                                    )}
                                                    {viewData.logo && (
                                                        <tr>
                                                            <td className="ps-4 py-3 text-muted fw-bold">DASHBOARD LOGO</td>
                                                            <td className="pe-4 py-3 text-end">
                                                                <img src={`${mediaUrl}${viewData.logo}`} alt="Logo" style={{ maxHeight: '40px', objectFit: 'contain' }} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </TabPane>

                                    {/* Tab 2 — User Policies */}
                                    <TabPane tabId="2">
                                        <div className="bg-white p-4 rounded shadow-sm mb-4">
                                            <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">User Terms & Conditions</h6>
                                            {viewData.user_term_condition ? (
                                                <div
                                                    className="ck-content"
                                                    style={{ fontSize: '14px', lineHeight: '1.7' }}
                                                    dangerouslySetInnerHTML={{ __html: viewData.user_term_condition }}
                                                />
                                            ) : (
                                                <em className="text-muted">Not set.</em>
                                            )}
                                        </div>
                                        <div className="bg-white p-4 rounded shadow-sm">
                                            <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">User Privacy Policy</h6>
                                            {viewData.user_privacy_policy ? (
                                                <div
                                                    className="ck-content"
                                                    style={{ fontSize: '14px', lineHeight: '1.7' }}
                                                    dangerouslySetInnerHTML={{ __html: viewData.user_privacy_policy }}
                                                />
                                            ) : (
                                                <em className="text-muted">Not set.</em>
                                            )}
                                        </div>
                                    </TabPane>

                                    {/* Tab 3 — Bystander Policies */}
                                    <TabPane tabId="3">
                                        <div className="bg-white p-4 rounded shadow-sm mb-4">
                                            <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">Bystander Terms & Conditions</h6>
                                            {viewData.bystander_term_condition ? (
                                                <div
                                                    className="ck-content"
                                                    style={{ fontSize: '14px', lineHeight: '1.7' }}
                                                    dangerouslySetInnerHTML={{ __html: viewData.bystander_term_condition }}
                                                />
                                            ) : (
                                                <em className="text-muted">Not set.</em>
                                            )}
                                        </div>
                                        <div className="bg-white p-4 rounded shadow-sm">
                                            <h6 className="fw-bold mb-3 pb-2 border-bottom text-primary">Bystander Privacy Policy</h6>
                                            {viewData.bystander_privacy_policy ? (
                                                <div
                                                    className="ck-content"
                                                    style={{ fontSize: '14px', lineHeight: '1.7' }}
                                                    dangerouslySetInnerHTML={{ __html: viewData.bystander_privacy_policy }}
                                                />
                                            ) : (
                                                <em className="text-muted">Not set.</em>
                                            )}
                                        </div>
                                    </TabPane>
                                </TabContent>
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal>

            <ToastContainer />
        </div>
    );
}
